import { useApolloClient } from "@apollo/client";
import { MenuItem, Text } from "@chakra-ui/react";
import React from "react";
import { Glossary } from "src/components/Text/Glossary";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { maybePluralize } from "src/services/format";
import { WaitlistError } from "../errors";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canRemoveFromWaitlists } from "../validations/removeFromWaitlists";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  menuLabel?: string;
  isBulkAction?: boolean;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "You will remove this form from its school and grade waitlist.",
    "You will remove these forms from each of the selected school and grades waitlists."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(
    count,
    "Yes, remove from waitlist",
    "Yes, remove from waitlists"
  );

export const RemoveFromWaitlists = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
  menuLabel = "Remove from waitlists",
  isBulkAction = false,
}: Props) => {
  const { mutateAsync: removeFromWaitListBulk } = FSQ.useDeleteWaitlists();

  const toast = useAvelaToast();
  const client = useApolloClient();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async (evt: React.MouseEvent) => {
    const schoolForms = selectedFormSchoolStatuses;
    const count = schoolForms.length;
    const trackArgs = {
      actionType: "REMOVE_FROM_WAITLIST" as const,
      bulk: isBulkAction,
      count,
      async: false,
    };

    const formNoun = maybePluralize(count, "form");

    trackFormAction({ verb: "Clicked", ...trackArgs });

    if (
      await confirm({
        header: (
          <Text>{`Remove ${count} ${formNoun} from ${maybePluralize(
            count,
            "waitlist"
          )}?`}</Text>
        ),
        body: (
          <Text>
            <Glossary>{confirmationBodyText(count)}</Glossary>
          </Text>
        ),
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      })
    ) {
      trackFormAction({ verb: "Confirmed", ...trackArgs });

      const loadingToastId = toast({
        title: `Working hard`,
        description: `Removing ${count} ${formNoun}...`,
        status: "loading",
        duration: null,
      });

      try {
        const response = await removeFromWaitListBulk({
          body: {
            data: {
              waitlists: schoolForms.map((schoolForm) => ({
                form_id: schoolForm.form_id,
                school_id: schoolForm.school_id,
              })),
            },
          },
        });

        await client.refetchQueries({
          include: [FETCH_OFFER_WAITLIST_STATUS, GET_ASSIGNED_FORMS_BY_FORM_ID],
        });

        if (onRefetch) onRefetch();

        const numRemoved = response.data?.data.num_removed;
        toast.close(loadingToastId);
        toast({
          description:
            numRemoved === undefined
              ? "Some forms removed from the waitlist"
              : `${numRemoved} ${maybePluralize(
                  numRemoved,
                  "form"
                )} removed from the waitlist`,
          status: "info",
        });
        trackFormAction({ verb: "Completed", ...trackArgs });
      } catch (err: any) {
        trackFormActionError(err, trackArgs);
        toast.close(loadingToastId);
        let message = "Check your network and try again.";

        if (err instanceof WaitlistError) {
          message = err.message;
        }

        toast.error({
          title: "Oops! Something went wrong!",
          description: message,
        });
        console.error(err);
      }
    } else {
      trackFormAction({ verb: "Canceled", ...trackArgs });
    }
  };

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canRemoveFromWaitlists(
            selectedFormSchoolStatuses,
            selectedFormSchools
          )
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
